import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./Header.css";
import AuthModal from "./components/AuthModal";
import logo from "../images/Mariamly Luxe - Logo.svg";

const API_BASE = "https://api.mariamlyluxe.com/public";
const STORAGE_BASE = `${API_BASE}/storage/`;

// read token from localStorage only (no cookies)
const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("auth_token") ||
  localStorage.getItem("access_token") ||
  "";

export default function Header() {
  // ========= URL & ACTIVE STATE =========
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const qs = new URLSearchParams(search);

  const isActiveHome = pathname === "/";
  const isActiveBrands = pathname.startsWith("/brands") || qs.has("brand");
  const isActiveShop =
    pathname.startsWith("/shop") ||
    pathname.startsWith("/categories") ||
    pathname.startsWith("/product") ||
    qs.has("category") ||
    qs.has("q");
  const isActiveOffers = pathname.startsWith("/offers");
  const isActiveContact = pathname.startsWith("/contact");

  // Active states for tile links
  const isActiveCart = pathname.startsWith("/cart");
  const isActiveAccount = pathname.startsWith("/account");
  const isActiveWishlist = pathname.startsWith("/wishlist");

  // ========= AUTH + CART =========
  const [token, setToken] = useState(getToken());
  const [isAuthed, setIsAuthed] = useState(!!token);
  const [userRole, setUserRole] = useState(localStorage.getItem("user_role"));
  const [cartCount, setCartCount] = useState(0);

  // modal + post-login redirect
  const [showAuth, setShowAuth] = useState(false);
  const [nextPath, setNextPath] = useState("");

  // brands
  const [brands, setBrands] = useState([]);
  const [brandsLoaded, setBrandsLoaded] = useState(false);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [brandsError, setBrandsError] = useState("");

  // categories
  const [cats, setCats] = useState([]);
  const [catsLoaded, setCatsLoaded] = useState(false);
  const [catsLoading, setCatsLoading] = useState(false);
  const [catsError, setCatsError] = useState("");

  // search
  const [q, setQ] = useState("");
  const [sLoading, setSLoading] = useState(false);
  const [sError, setSError] = useState("");
  const [results, setResults] = useState([]); // {id, name, image_url, price, slug}
  const [openSearchDD, setOpenSearchDD] = useState(false);
  const [hi, setHi] = useState(-1);
  const searchWrapRef = useRef(null);
  const debounceRef = useRef(null);

  // mobile menu
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // ===== helper: extract cart count regardless of shape =====
  const extractCartCount = (data) => {
    const candidates =
      data?.items ??
      data?.cart?.items ??
      data?.cart ??
      data?.products ??
      data?.data ??
      [];

    if (Array.isArray(candidates)) {
      return candidates.length;
    }
    if (candidates && typeof candidates === "object") {
      return Object.keys(candidates).length;
    }
    if (Number.isFinite(+data?.total_items)) return +data.total_items;
    return 0;
  };

  // ===== single place to fetch cart =====
  const fetchCart = async (tkn) => {
    try {
      const res = await fetch(`${API_BASE}/api/cart`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${tkn}`,
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (res.status === 401 || res.status === 419) {
        setCartCount(0);
        setNextPath("/cart");
        setShowAuth(true);
        return;
      }
      if (!res.ok) throw new Error(`Cart fetch failed: ${res.status}`);

      const data = await res.json();
      setCartCount(extractCartCount(data));
    } catch (e) {
      console.warn("loadCart error:", e);
      setCartCount(0);
    }
  };

  // keep auth in sync with localStorage changes and tab focus
  useEffect(() => {
    const syncAuth = () => {
      const t = getToken();
      const role = localStorage.getItem("user_role");
      setToken(t);
      setIsAuthed(!!t);
      setUserRole(role);
    };
    const onStorage = (ev) => {
      if (["token", "auth_token", "access_token", "user_role"].includes(ev.key)) syncAuth();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", syncAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", syncAuth);
    };
  }, []);

  // load cart badge (only when authed)
  useEffect(() => {
    if (!isAuthed || !token) {
      setCartCount(0);
      return;
    }
    fetchCart(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, token]);

  // Auto-open auth removed - users can access auth modal manually when needed

  // loaders
  const loadBrands = async () => {
    if (brandsLoaded || brandsLoading) return;
    try {
      setBrandsLoading(true);
      setBrandsError("");
      const res = await fetch(`${API_BASE}/api/brands`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`Brands fetch failed: ${res.status}`);
      const j = await res.json();
      console.log('Raw API response:', j); // Debug log
      const list = Array.isArray(j?.brands)
        ? j.brands
        : Array.isArray(j?.data)
        ? j.data
        : [];
      console.log('Brands list:', list); // Debug log
      const processedBrands = list.map((b) => ({
        id: b.id,
        name: b.name || b.title || `Brand #${b.id}`,
        image_url: b.image_url || b.image || b.logo_url || null,
        slug: b.slug || null,
        is_active: b.is_active ?? true,
        sort_order: parseInt(b.sort_order) || 0,
      }));
      console.log('Processed brands:', processedBrands); // Debug log
      
      // Sort brands by sort_order (ascending)
      const sortedBrands = processedBrands.sort((a, b) => a.sort_order - b.sort_order);
      setBrands(sortedBrands);
      setBrandsLoaded(true);
    } catch (err) {
      console.warn(err);
      setBrandsError("Failed to load brands.");
    } finally {
      setBrandsLoading(false);
    }
  };

  const loadCats = async () => {
    if (catsLoaded || catsLoading) return;
    try {
      setCatsLoading(true);
      setCatsError("");
      const res = await fetch(`${API_BASE}/api/categories`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`Categories fetch failed: ${res.status}`);
      const j = await res.json();
      const list = Array.isArray(j?.categories)
        ? j.categories
        : Array.isArray(j?.data)
        ? j.data
        : [];
      setCats(
        list.map((c) => ({
          id: c.id,
          name: c.name || c.title || `Category #${c.id}`,
          image_url: c.image_url || c.image || c.icon_url || null,
          slug: c.slug || null,
          is_active: c.is_active ?? true,
        }))
      );
      setCatsLoaded(true);
    } catch (err) {
      console.warn(err);
      setCatsError("Failed to load categories.");
    } finally {
      setCatsLoading(false);
    }
  };

  // ---------- AUTH GUARD ----------
  const guard = (path) => (e) => {
    if (!isAuthed) {
      e.preventDefault();
      setNextPath(path);
      setShowAuth(true);
    }
  };

  const handleAuthSuccess = () => {
    const t = getToken(); // AuthModal should store the token in localStorage
    const role = localStorage.getItem("user_role");
    setToken(t);
    setIsAuthed(!!t);
    setUserRole(role);
    setShowAuth(false);
    if (nextPath) navigate(nextPath);
    if (t) fetchCart(t);
  };

  // url builders
  const brandHref = (b) =>
    b.slug
      ? `/brands/${encodeURIComponent(b.slug)}`
      : `/shop?brand_id=${encodeURIComponent(b.id)}`;
  const catHref = (c) =>
    c.slug
      ? `/categories/${encodeURIComponent(c.slug)}`
      : `/shop?category_id=${encodeURIComponent(c.id)}`;

  // search
  const normalizeProduct = (p) => {
    const img =
      p.image_url ||
      p.images?.[0]?.url ||
      (p.images?.[0]?.path
        ? p.images?.[0]?.path.startsWith("http")
          ? p.images?.[0]?.path
          : STORAGE_BASE + p.images?.[0]?.path
        : null);

    return {
      id: p.id,
      name: p.name || "",
      price: p.selling_price ?? p.regular_price ?? 0,
      image_url: img,
      slug: p.slug || null,
    };
  };

  // Helpers to make search more forgiving
  const stripDiacritics = (s) =>
    s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const generateQueryVariants = (input) => {
    const base = String(input || '').trim();
    if (!base) return [];

    const lower = base.toLowerCase();
    const noApos = lower.replace(/[’']/g, '');
    const noPunct = lower.replace(/[^a-z0-9\s]/gi, ' ');
    const noDiac = stripDiacritics(lower);

    // If it starts with a single letter followed by letters and has no apostrophe,
    // also try adding an apostrophe after the first letter: e.g. loreal -> l'oreal
    let apostropheVariant = null;
    const m = noDiac.match(/^\s*([a-z])\s*([a-z]{2,}.*)$/i);
    if (m && !/[’']/.test(lower)) {
      apostropheVariant = `${m[1]}'${m[2].replace(/^\s+/, '')}`;
    }

    const variants = [
      base,
      lower,
      noDiac,
      noApos,
      noPunct.replace(/\s+/g, ' ').trim(),
      apostropheVariant,
    ]
      .filter(Boolean)
      .map((v) => v.trim())
      .filter((v, i, arr) => v.length >= 2 && arr.indexOf(v) === i);

    return variants;
  };

  const fetchSearch = async (term) => {
    const variants = generateQueryVariants(term);

    setSLoading(true);
    setSError("");

    let found = [];
    let lastError = null;

    for (const v of variants) {
      try {
        const url = `${API_BASE}/api/products/search?q=${encodeURIComponent(v)}&per_page=8`;
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(`Search failed (${v}): ${res.status}`);
        const j = await res.json();
        const list = Array.isArray(j?.products?.data)
          ? j.products.data
          : Array.isArray(j?.products)
          ? j.products
          : [];
        if (Array.isArray(list) && list.length > 0) {
          found = list.map(normalizeProduct);
          break;
        }
      } catch (err) {
        lastError = err;
        // continue to next variant
      }
    }

    if (found.length > 0) {
      setResults(found);
      setOpenSearchDD(true);
      setHi(-1);
      setSLoading(false);
      return;
    }

    // If we reach here, either all calls failed or no results from any variant
    if (lastError) {
      console.warn(lastError);
    }
    setResults([]);
    setOpenSearchDD(true);
    setHi(-1);
    setSLoading(false);
  };

  const onChangeSearch = (e) => {
    const v = e.target.value;
    setQ(v);
    setSError("");
    setHi(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (v.trim().length < 2) {
      setResults([]);
      setOpenSearchDD(false);
      return;
    }
    debounceRef.current = setTimeout(() => fetchSearch(v.trim()), 250);
  };

  const onFocusSearch = () => {
    if (results.length > 0 || sLoading || sError) setOpenSearchDD(true);
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (!searchWrapRef.current) return;
      if (!searchWrapRef.current.contains(e.target)) {
        setOpenSearchDD(false);
        setHi(-1);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const onKeyDownSearch = (e) => {
    if (!openSearchDD) return;
    const max = results.length - 1;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHi((p) => (p < max ? p + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((p) => (p > 0 ? p - 1 : max));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (hi >= 0 && results[hi]) {
        goToProduct(results[hi]);
      } else if (q.trim()) {
        navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
        setOpenSearchDD(false);
      }
    } else if (e.key === "Escape") {
      setOpenSearchDD(false);
      setHi(-1);
    }
  };

  const goToProduct = (p) => {
    setOpenSearchDD(false);
    setHi(-1);
    if (p.slug) navigate(`/product/${encodeURIComponent(p.slug)}`);
    else navigate(`/product/${p.id}`);
  };

  // mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((v) => !v);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // ===== UX: lock body scroll when mobile menu is open =====
  useEffect(() => {
    const body = document.body;
    if (isMobileMenuOpen) {
      body.classList.add("no-scroll");
    } else {
      body.classList.remove("no-scroll");
    }
    return () => body.classList.remove("no-scroll");
  }, [isMobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);

  // Close with Esc
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
        setOpenSearchDD(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header className="h-wrap">
      <div className="h-inner">
        {/* Left: Logo + Nav */}
        <div className="h-left">
          <a className="h-logo" href="/">
            <img src={logo} alt="Mariamly" className="logo-img" />
          </a>

          {/* Mobile Burger Button */}
          <button
            className={`mobile-burger ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu-panel"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <nav className="h-nav" aria-label="Primary">
            <a className={isActiveHome ? "active" : ""} href="/">Home</a>

            {/* BRANDS */}
            <div className="dd-wrap" onMouseEnter={loadBrands} onFocusCapture={loadBrands}>
              <a
                href="/brands"
                className={`has-dd ${isActiveBrands ? "active" : ""}`}
                aria-haspopup="true"
              >
                Brands
                <svg viewBox="0 0 24 24" className="chev" aria-hidden="true">
                  <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.6" />
                </svg>
              </a>

              <div className="dd-panel brands-dd" role="menu" aria-label="All Brands">
                <div className="dd-head">
                  <span>All Brands</span>
                  <Link className="view-all" to="/brands">View all</Link>
                </div>

                {brandsLoading && <div className="dd-state">Loading…</div>}
                {!!brandsError && !brandsLoading && <div className="dd-state error">{brandsError}</div>}
                {!brandsLoading && !brandsError && brands.length === 0 && (
                  <div className="dd-state">No brands found.</div>
                )}

                {!brandsLoading && brands.length > 0 && (
                  <ul className="brand-list">
                    {brands.map((b) => {
                      console.log('Brand data:', b); // Debug log
                      return (
                        <li key={b.id}>
                          <a className="brand-item" href={brandHref(b)}>
                            <div className="brand-thumb">
                              {b.image_url ? (
                                <img src={b.image_url} alt={b.name} loading="lazy" />
                              ) : (
                                <div className="brand-placeholder" aria-hidden="true">
                                  {b.name?.[0]?.toUpperCase() || "B"}
                                </div>
                              )}
                            </div>
                            <div className="brand-name brandsnames">{b.name || 'No Name'}</div>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            {/* SHOP -> CATEGORIES */}
            <div className="dd-wrap" onMouseEnter={loadCats} onFocusCapture={loadCats}>
              <a
                href="/shop"
                className={`has-dd ${isActiveShop ? "active" : ""}`}
                aria-haspopup="true"
              >
                Shop
                <svg viewBox="0 0 24 24" className="chev" aria-hidden="true">
                  <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.6" />
                </svg>
              </a>

              <div className="dd-panel cats-dd" role="menu" aria-label="Shop by Category">
                <div className="dd-head">
                  <span>Shop by Category</span>
                  <Link className="view-all" to="/shop">View all</Link>
                </div>

                {catsLoading && <div className="dd-state">Loading…</div>}
                {!!catsError && !catsLoading && <div className="dd-state error">{catsError}</div>}
                {!catsLoading && !catsError && cats.length === 0 && (
                  <div className="dd-state">No categories found.</div>
                )}

                {!catsLoading && cats.length > 0 && (
                  <ul className="cat-list">
                    {cats.map((c) => (
                      <li key={c.id}>
                        <a className="cat-item" href={catHref(c)}>
                          <div className="cat-thumb">
                            {c.image_url ? (
                              <img src={c.image_url} alt={c.name} loading="lazy" />
                            ) : (
                              <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M4 7h16v10H4z" fill="none" stroke="currentColor" strokeWidth="1.2" />
                                <path d="M4 9l8 5 8-5" fill="none" stroke="currentColor" strokeWidth="1.2" />
                              </svg>
                            )}
                          </div>
                          <div className="cat-info">
                            <div className="cat-name">{c.name}</div>
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <a className={isActiveOffers ? "active" : ""} href="/offers">Offers</a>
            <a className={isActiveContact ? "active" : ""} href="/contact">Contact</a>

            {/* Admin Dashboard Link - Only show for admin users */}
            {isAuthed && userRole === "1" && (
              <a className={pathname.startsWith("/admin") ? "active" : ""} href="/admin">
                Admin
              </a>
            )}
          </nav>
        </div>

        {/* Right: Divider + Search + Tiles */}
        <div className="h-right">
          <div className="vdiv" aria-hidden="true" />

          {/* SEARCH */}
          <div className="h-search search-wrap" ref={searchWrapRef}>
            <svg viewBox="0 0 24 24" className="s-icon" aria-hidden="true">
              <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <path d="M20 20l-3.2-3.2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              placeholder="Type for search"
              aria-label="Search products"
              value={q}
              onChange={onChangeSearch}
              onFocus={onFocusSearch}
              onKeyDown={onKeyDownSearch}
            />

            {openSearchDD && (
              <div className="search-dd" role="listbox" aria-label="Search results">
                {sLoading && <div className="search-state">Searching…</div>}
                {!!sError && !sLoading && <div className="search-state error">{sError}</div>}

                {!sLoading && !sError && results.length === 0 && q.trim().length >= 2 && (
                  <div className="search-state">No results for “{q}”.</div>
                )}

                {!sLoading && results.length > 0 && (
                  <ul className="search-list">
                    {results.map((p, idx) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          className={`search-item ${hi === idx ? "is-active" : ""}`}
                          onMouseEnter={() => setHi(idx)}
                          onMouseLeave={() => setHi(-1)}
                          onClick={() => goToProduct(p)}
                          role="option"
                          aria-selected={hi === idx}
                        >
                          <div className="s-thumb">
                            {p.image_url ? <img src={p.image_url} alt={p.name} loading="lazy" /> : <div className="noimg" />}
                          </div>
                          <div className="s-meta">
                            <div className="s-name">{p.name}</div>
                            <div className="s-price">
                              {Number(p.price).toLocaleString(undefined, { minimumFractionDigits: 0 })} $
                            </div>
                          </div>
                        </button>
                      </li>
                    ))}
                    <li>
                      <Link
                        className="search-all"
                        to={`/shop?q=${encodeURIComponent(q.trim())}`}
                        onClick={() => setOpenSearchDD(false)}
                      >
                        Search all results for “{q}”
                      </Link>
                    </li>
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* tiles — guarded */}
          <Link to="/cart" className={`tile tile-badge ${isActiveCart ? "active" : ""}`} aria-label="Cart" onClick={guard("/cart")}>
            <svg viewBox="0 0 24 24">
              <path
                d="M7 6h13l-1.2 7.2a2 2 0 0 1-2 1.8H9.3a2 2 0 0 1-2-1.7L6 4H3"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
              <circle cx="10" cy="19" r="1" />
              <circle cx="17" cy="19" r="1" />
            </svg>
            {isAuthed && cartCount > 0 && (
              <span className="count-badge" aria-label={`${cartCount} items in cart`}>{cartCount}</span>
            )}
          </Link>

          <Link to="/account" className={`tile ${isActiveAccount ? "active" : ""}`} aria-label="Account" onClick={guard("/account")}>
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="8" r="3.25" fill="none" stroke="currentColor" strokeWidth="1.4" />
              <path d="M4.5 20c1.6-3.6 5-5 7.5-5s5.9 1.4 7.5 5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </Link>

          <Link to="/wishlist" className={`tile ${isActiveWishlist ? "active" : ""}`} aria-label="Wishlist" onClick={guard("/wishlist")}>
            <svg viewBox="0 0 24 24">
              <path
                d="M12 20s-7-4.3-7-9.3C5 8 6.7 6.3 8.9 6.3c1.5 0 2.6.7 3.1 1.7.5-1 1.6-1.7 3.1-1.7 2.2 0 3.9 1.7 3.9 4.4 0 5-7 9.3-7 9.3z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={closeMobileMenu}>
          <div
            id="mobile-menu-panel"
            className="mobile-menu"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
          >
            <div className="mobile-menu-header">
              <div className="mobile-menu-logo">
                <a href="/" onClick={closeMobileMenu}>
                  <img src={logo} alt="Mariamly" className="logo-img" />
                </a>
              </div>
              <button
                className="mobile-menu-close"
                onClick={closeMobileMenu}
                aria-label="Close mobile menu"
              >
                <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <nav className="mobile-nav">
              <a className={isActiveHome ? "active" : ""} href="/" onClick={closeMobileMenu}>Home</a>
              <a className={isActiveBrands ? "active" : ""} href="/brands" onClick={closeMobileMenu}>Brands</a>
              <a className={isActiveShop ? "active" : ""} href="/shop" onClick={closeMobileMenu}>Shop</a>
              <a className={isActiveOffers ? "active" : ""} href="/offers" onClick={closeMobileMenu}>Offers</a>
              <a className={isActiveContact ? "active" : ""} href="/contact" onClick={closeMobileMenu}>Contact</a>

              {isAuthed && userRole === "1" && (
                <a className={pathname.startsWith("/admin") ? "active" : ""} href="/admin" onClick={closeMobileMenu}>
                  Admin
                </a>
              )}
            </nav>

            <div className="mobile-menu-tiles">
              <Link
                to="/cart"
                className={`mobile-tile ${isActiveCart ? "active" : ""}`}
                onClick={(e) => {
                  closeMobileMenu();
                  guard("/cart")(e);
                }}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M7 6h13l-1.2 7.2a2 2 0 0 1-2 1.8H9.3a2 2 0 0 1-2-1.7L6 4H3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                  <circle cx="10" cy="19" r="1" />
                  <circle cx="17" cy="19" r="1" />
                </svg>
                <span>Cart</span>
                {isAuthed && cartCount > 0 && <span className="mobile-count-badge">{cartCount}</span>}
              </Link>

              <Link
                to="/account"
                className={`mobile-tile ${isActiveAccount ? "active" : ""}`}
                onClick={(e) => {
                  closeMobileMenu();
                  guard("/account")(e);
                }}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="8" r="3.25" fill="none" stroke="currentColor" strokeWidth="1.4" />
                  <path d="M4.5 20c1.6-3.6 5-5 7.5-5s5.9 1.4 7.5 5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
                <span>Account</span>
              </Link>

              <Link
                to="/wishlist"
                className={`mobile-tile ${isActiveWishlist ? "active" : ""}`}
                onClick={(e) => {
                  closeMobileMenu();
                  guard("/wishlist")(e);
                }}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M12 20s-7-4.3-7-9.3C5 8 6.7 6.3 8.9 6.3c1.5 0 2.6.7 3.1 1.7.5-1 1.6-1.7 3.1-1.7 2.2 0 3.9 1.7 3.9 4.4 0 5-7 9.3-7 9.3z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Wishlist</span>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Auth modal */}
      {showAuth && (
        <AuthModal
          isOpen={true}
          onClose={() => setShowAuth(false)}
          onLoginSuccess={handleAuthSuccess}
          onSuccess={handleAuthSuccess}
          apiBase={API_BASE}
        />
      )}
    </header>
  );
}
